import { Group, Stack, createStyles } from '@mantine/core';
import type { ScheduleSettings, ScheduleType } from '@repo/api-codegen';
import { Banner, Select, Text, TextInput } from '@repo/foundations';
import cronstrue from 'cronstrue';
import { useCallback, useMemo, useRef } from 'react';
import {
	DAYS_OF_MONTH,
	DAYS_OF_WEEK,
	HOURS_OF_DAY,
	scheduleTypeOptions,
} from './constants';

const DEFAULT_MINUTE = 0;

interface ScheduleInputProps {
	cronExpression: string;
	onChange: (cronExpression: string) => void;
}

const useStyles = createStyles((theme) => ({
	group: {
		gap: theme.spacing.sm,
		padding: 0,
	},
}));

// Helper function to parse cron expression and extract components
function parseCronExpression(cronExpression: string): {
	scheduleType: ScheduleType;
	hourOfDay?: number;
	dayOfWeek?: number;
	dayOfMonth?: number;
} {
	if (!cronExpression) {
		return { scheduleType: 'daily', hourOfDay: 9, dayOfWeek: 1 };
	}

	try {
		const parts = cronExpression.split(' ');
		if (parts.length >= 5) {
			const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

			// Check for hourly pattern: must be exactly "0 * * * *"
			if (
				minute === '0' &&
				hour === '*' &&
				dayOfMonth === '*' &&
				month === '*' &&
				dayOfWeek === '*'
			) {
				return { scheduleType: 'hourly' };
			}

			// Check for daily pattern: must be exactly "0 <hour> * * *" where hour is a simple integer
			if (
				minute === '0' &&
				dayOfMonth === '*' &&
				month === '*' &&
				dayOfWeek === '*' &&
				/^\d+$/.test(hour)
			) {
				const hourValue = parseInt(hour);
				if (!isNaN(hourValue) && hourValue >= 0 && hourValue <= 23) {
					return { scheduleType: 'daily', hourOfDay: hourValue };
				}
			}

			// Check for weekly pattern: must be exactly "0 <hour> * * <dayOfWeek>" where both are simple integers
			if (
				minute === '0' &&
				dayOfMonth === '*' &&
				month === '*' &&
				dayOfWeek !== '*' &&
				/^\d+$/.test(hour) &&
				/^\d+$/.test(dayOfWeek)
			) {
				const hourValue = parseInt(hour);
				const dayValue = parseInt(dayOfWeek);
				if (
					!isNaN(hourValue) &&
					hourValue >= 0 &&
					hourValue <= 23 &&
					!isNaN(dayValue) &&
					dayValue >= 0 &&
					dayValue <= 6
				) {
					return {
						scheduleType: 'weekly',
						hourOfDay: hourValue,
						dayOfWeek: dayValue,
					};
				}
			}

			// Check for monthly pattern: must be exactly "0 <hour> <dayOfMonth> * *" where both are simple integers
			if (
				minute === '0' &&
				dayOfMonth !== '*' &&
				month === '*' &&
				dayOfWeek === '*' &&
				/^\d+$/.test(hour) &&
				/^\d+$/.test(dayOfMonth)
			) {
				const hourValue = parseInt(hour);
				const dayValue = parseInt(dayOfMonth);
				if (
					!isNaN(hourValue) &&
					hourValue >= 0 &&
					hourValue <= 23 &&
					!isNaN(dayValue) &&
					dayValue >= 1 &&
					dayValue <= 31
				) {
					return {
						scheduleType: 'monthly',
						hourOfDay: hourValue,
						dayOfMonth: dayValue,
					};
				}
			}
		}
	} catch {
		// If parsing fails, treat as custom (cron)
	}

	// Doesn't match any standard pattern - treat as custom cron
	return { scheduleType: 'cron' };
}

export function ScheduleInput({
	cronExpression,
	onChange,
}: ScheduleInputProps) {
	const { classes } = useStyles();

	// Parse the cron expression to extract components
	const parsedCron = useMemo(
		() => parseCronExpression(cronExpression),
		[cronExpression]
	);

	// Track the selected schedule type separately from parsed cron
	// This allows users to explicitly choose "custom" even if the cron matches a standard pattern
	const selectedTypeRef = useRef<ScheduleType | 'monthly'>(
		parsedCron.scheduleType as ScheduleType | 'monthly'
	);
	const prevCronRef = useRef(cronExpression);
	const isCustomInputChangeRef = useRef(false);

	// If cron expression changed externally (not from our handlers), update the selected type
	// But don't update if the change came from the custom cron input while in cron mode
	if (prevCronRef.current !== cronExpression) {
		prevCronRef.current = cronExpression;

		// Only auto-update the selected type if:
		// 1. The change didn't come from custom input, OR
		// 2. The user is not currently in cron mode
		if (!isCustomInputChangeRef.current || selectedTypeRef.current !== 'cron') {
			selectedTypeRef.current = parsedCron.scheduleType;
		}

		// Reset the flag after processing
		isCustomInputChangeRef.current = false;
	}

	// Derive schedule settings using the selected type and parsed components
	// Not memoized since it depends on a ref that's not in the dependency array
	const scheduleSettings: ScheduleSettings & { day_of_month?: number } = {
		schedule_type: selectedTypeRef.current as ScheduleType,
		hour_of_day: parsedCron.hourOfDay ?? 9,
		day_of_week: parsedCron.dayOfWeek ?? 1,
		day_of_month: parsedCron.dayOfMonth ?? 1,
		cron_expression: cronExpression || '0 9 * * *',
	};

	const showDayInput = useMemo(
		() => scheduleSettings.schedule_type === 'weekly',
		[scheduleSettings.schedule_type]
	);

	const showDayOfMonthInput = useMemo(
		() => (scheduleSettings.schedule_type as string) === 'monthly',
		[scheduleSettings.schedule_type]
	);

	const showHourInput = useMemo(
		() =>
			['daily', 'weekly', 'monthly'].includes(
				scheduleSettings.schedule_type as string
			),
		[scheduleSettings.schedule_type]
	);

	const showCustomInput = useMemo(
		() => scheduleSettings.schedule_type === 'cron',
		[scheduleSettings.schedule_type]
	);

	const dayAndHourSelectWidth = useMemo(
		() =>
			(showDayInput || showDayOfMonthInput) && showHourInput ? '48.5%' : '100%',
		[showDayInput, showDayOfMonthInput, showHourInput]
	);

	// Compute human readable schedule and validation
	const scheduleValidation = useMemo(() => {
		if (cronExpression) {
			try {
				const readable = cronstrue.toString(cronExpression);
				return {
					humanReadable: readable,
					invalid: false,
					tooFrequent: false,
				};
			} catch {
				return {
					humanReadable: undefined,
					invalid: false,
					tooFrequent: false,
				};
			}
		}
		return {
			humanReadable: undefined,
			invalid: false,
			tooFrequent: false,
		};
	}, [cronExpression]);

	const handleScheduleTypeChange = useCallback(
		(newType: string) => {
			const scheduleType = newType as ScheduleType | 'monthly';

			// Update the selected type
			selectedTypeRef.current = scheduleType;

			// Parse current cron to get hour and day values
			const parsed = parseCronExpression(cronExpression);

			// Generate and emit new cron expression based on new schedule type
			const minute = DEFAULT_MINUTE;
			const hour = parsed.hourOfDay ?? 9;
			const dayOfWeek = parsed.dayOfWeek ?? 1;
			const dayOfMonth = parsed.dayOfMonth ?? 1;
			let newCron = '';

			switch (scheduleType) {
				case 'hourly':
					newCron = `0 * * * *`;
					break;
				case 'daily':
					newCron = `${minute} ${hour} * * *`;
					break;
				case 'weekly':
					newCron = `${minute} ${hour} * * ${dayOfWeek}`;
					break;
				case 'monthly':
					newCron = `${minute} ${hour} ${dayOfMonth} * *`;
					break;
				case 'cron':
					// Keep the existing cron expression or use a default
					newCron = cronExpression || '0 9 * * *';
					break;
				default:
					newCron = `${minute} ${hour} * * *`; // Default to daily
					break;
			}

			onChange(newCron);
		},
		[cronExpression, onChange]
	);

	const handleCustomCronChange = useCallback(
		(newCron: string) => {
			// Mark that this change came from the custom input
			isCustomInputChangeRef.current = true;
			onChange(newCron);
		},
		[onChange]
	);

	const handleDayOfWeekChange = useCallback(
		(newDay: string) => {
			const dayOfWeek = Number(newDay);

			// Parse current cron to get hour value
			const parsed = parseCronExpression(cronExpression);

			// Generate new cron expression with updated day
			const minute = DEFAULT_MINUTE;
			const hour = parsed.hourOfDay ?? 9;
			let newCron = '';

			switch (selectedTypeRef.current) {
				case 'hourly':
					newCron = `0 * * * *`;
					break;
				case 'daily':
					newCron = `${minute} ${hour} * * *`;
					break;
				case 'weekly':
					newCron = `${minute} ${hour} * * ${dayOfWeek}`;
					break;
				case 'monthly': {
					const parsedForDay = parseCronExpression(cronExpression);
					const dayOfMonthForDay = parsedForDay.dayOfMonth ?? 1;
					newCron = `${minute} ${hour} ${dayOfMonthForDay} * *`;
					break;
				}
				default:
					newCron = `${minute} ${hour} * * *`; // Default to daily
					break;
			}

			onChange(newCron);
		},
		[cronExpression, onChange]
	);

	const handleDayOfMonthChange = useCallback(
		(newDay: string) => {
			const dayOfMonth = Number(newDay);

			// Parse current cron to get hour value
			const parsed = parseCronExpression(cronExpression);

			// Generate new cron expression with updated day of month
			const minute = DEFAULT_MINUTE;
			const hour = parsed.hourOfDay ?? 9;
			let newCron = '';

			switch (selectedTypeRef.current) {
				case 'hourly':
					newCron = `0 * * * *`;
					break;
				case 'daily':
					newCron = `${minute} ${hour} * * *`;
					break;
				case 'weekly': {
					const dayOfWeek = parsed.dayOfWeek ?? 1;
					newCron = `${minute} ${hour} * * ${dayOfWeek}`;
					break;
				}
				case 'monthly':
					newCron = `${minute} ${hour} ${dayOfMonth} * *`;
					break;
				default:
					newCron = `${minute} ${hour} * * *`; // Default to daily
					break;
			}

			onChange(newCron);
		},
		[cronExpression, onChange]
	);

	const handleHourOfDayChange = useCallback(
		(newHour: string) => {
			const hourOfDay = Number(newHour);

			// Parse current cron to get day value
			const parsed = parseCronExpression(cronExpression);

			// Generate new cron expression with updated hour
			const minute = DEFAULT_MINUTE;
			const dayOfWeek = parsed.dayOfWeek ?? 1;
			let newCron = '';

			switch (selectedTypeRef.current) {
				case 'hourly':
					newCron = `0 * * * *`;
					break;
				case 'daily':
					newCron = `${minute} ${hourOfDay} * * *`;
					break;
				case 'weekly':
					newCron = `${minute} ${hourOfDay} * * ${dayOfWeek}`;
					break;
				case 'monthly': {
					const parsedForHour = parseCronExpression(cronExpression);
					const dayOfMonthForHour = parsedForHour.dayOfMonth ?? 1;
					newCron = `${minute} ${hourOfDay} ${dayOfMonthForHour} * *`;
					break;
				}
				default:
					newCron = `${minute} ${hourOfDay} * * *`; // Default to daily
					break;
			}

			onChange(newCron);
		},
		[cronExpression, onChange]
	);

	return (
		<Stack spacing="md">
			<Select
				label="Frequency"
				value={scheduleSettings.schedule_type}
				onChange={handleScheduleTypeChange}
				data={scheduleTypeOptions}
			/>

			{showCustomInput && (
				<Stack spacing="3xs">
					<TextInput
						label="Cron expression"
						placeholder="0 9 * * *"
						value={scheduleSettings.cron_expression || ''}
						onChange={(event) =>
							handleCustomCronChange(event.currentTarget.value)
						}
					/>
					{scheduleValidation.humanReadable && !scheduleValidation.invalid && (
						<Text size="xs" color="text/secondary/default">
							{scheduleValidation.humanReadable}
						</Text>
					)}
					<Text size="xs" color="text/secondary/default">
						Use{' '}
						<a
							href="https://crontab.guru"
							target="_blank"
							rel="noopener noreferrer"
						>
							crontab.guru
						</a>{' '}
						to help build your cron expression
					</Text>
				</Stack>
			)}

			{(showDayInput || showDayOfMonthInput || showHourInput) && (
				<Group className={classes.group}>
					{showDayInput && (
						<Select
							label="Day"
							variant="default"
							size="md"
							placeholder="Enter a day"
							value={String(scheduleSettings.day_of_week ?? 1)}
							onChange={handleDayOfWeekChange}
							data={DAYS_OF_WEEK}
							width={dayAndHourSelectWidth}
						/>
					)}
					{showDayOfMonthInput && (
						<Select
							label="Day of Month"
							variant="default"
							size="md"
							placeholder="Enter a day"
							value={String(scheduleSettings.day_of_month ?? 1)}
							onChange={handleDayOfMonthChange}
							data={DAYS_OF_MONTH}
							width={dayAndHourSelectWidth}
						/>
					)}
					{showHourInput && (
						<Select
							label="Hour"
							variant="default"
							size="md"
							placeholder="Enter an hour"
							value={String(scheduleSettings.hour_of_day ?? 9)}
							onChange={handleHourOfDayChange}
							data={HOURS_OF_DAY}
							width={dayAndHourSelectWidth}
						/>
					)}
				</Group>
			)}

			{scheduleValidation.invalid && (
				<Banner
					message="Invalid cron expression. Please refer to crontab.guru for help."
					tone="critical"
					inCard
				/>
			)}

			{scheduleValidation.tooFrequent && (
				<Banner
					message="Cron expression must be hourly or less frequent."
					tone="critical"
					inCard
				/>
			)}
		</Stack>
	);
}
